import React, { useState } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { usePatchDraft } from '@/hooks/use-onboarding-interview';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { formatQuota } from '@/utils/format-quota';
import {
  EditableService,
  NEW_SERVICE,
  ServiceEditSheet,
} from './service-edit-sheet';
import { WidgetCard } from './widget-card';
import { ServiceListWidgetData, WidgetProps } from './widget-types';

/**
 * Os serviços com preço e duração — o cartão que responde os passos de valor e
 * de tempo do roteiro.
 *
 * O que o ramo cobra aparece em cinza como sugestão; **"Pronto" aceita o que
 * sobrou em cinza** e segue. Quem cobra o de sempre responde os dois passos
 * com um toque; quem cobra diferente toca no serviço e corrige. Tudo isso é
 * `PATCH /draft`: não passa pelo modelo e não gasta turno.
 */
export function ServiceListWidget({
  data,
  editable,
  onAdvance,
}: WidgetProps<ServiceListWidgetData>) {
  const [editing, setEditing] = useState<EditableService | null>(null);
  const patch = usePatchDraft();

  const canEdit = editable && data.editable !== false;
  const services = data.services ?? [];
  /** O que ainda está em cinza: sugestão do ramo que ninguém confirmou. */
  const pending = services.filter(
    service =>
      service.answeredPrice === false || service.answeredDuration === false,
  );

  /**
   * "Pronto" grava o que sobrou em cinza.
   *
   * Sem isto o botão avançaria a conversa deixando os passos de valor e de
   * tempo em aberto, e o fecho da entrevista completaria com o valor do ramo
   * de qualquer jeito — só que sem o prestador ter visto na tela o que aceitou.
   */
  const handleConfirm = async () => {
    const accepted = pending.map(service => ({
      name: service.name,
      ...(service.answeredPrice === false
        ? { defaultPrice: service.suggestedPrice ?? null }
        : {}),
      ...(service.answeredDuration === false
        ? { durationMinutes: service.suggestedDuration ?? null }
        : {}),
    }));

    if (accepted.length > 0) {
      await patch.mutateAsync({ payload: { services: accepted } });
    }
    onAdvance?.();
  };

  return (
    <>
      <WidgetCard title="O que você faz" icon="pricetags-outline">
        <View className="gap-2">
          {services.map(service => (
            <PressableScale
              key={service.name}
              disabled={!canEdit}
              onPress={() => setEditing(service)}
              className="flex-row items-center gap-3 rounded-xl bg-neutral-50 px-3 py-2.5"
            >
              <View className="flex-1 gap-0.5">
                <View className="flex-row items-center gap-1.5">
                  <ThemedText className="text-sm font-medium text-foreground">
                    {service.name}
                  </ThemedText>
                  {service.recurring ? (
                    <View className="rounded-full bg-primary/10 px-2 py-0.5">
                      <ThemedText className="text-[10px] font-semibold text-primary">
                        {service.subscriptionQuota
                          ? formatQuota(
                              service.subscriptionQuota,
                              service.subscriptionQuotaPeriod ??
                                QUOTA_PERIOD.MONTH,
                            )
                          : 'Assinatura'}
                      </ThemedText>
                    </View>
                  ) : null}
                </View>
                <ThemedText
                  className={`text-xs ${
                    service.answeredDuration === false
                      ? 'text-foreground/35'
                      : 'text-foreground/50'
                  }`}
                >
                  {service.answeredDuration === false
                    ? (service.suggestedDurationLabel ?? service.durationLabel)
                    : service.durationLabel}
                </ThemedText>
              </View>
              <ThemedText
                className={`text-sm font-semibold ${
                  service.answeredPrice === false
                    ? 'text-foreground/35'
                    : 'text-foreground'
                }`}
              >
                {service.answeredPrice === false
                  ? (service.suggestedPriceLabel ?? service.priceLabel)
                  : service.priceLabel}
              </ThemedText>
              {canEdit ? (
                <Ionicons className="text-neutral-400" name="create-outline" size={15} />
              ) : null}
            </PressableScale>
          ))}

          {canEdit ? (
            <PressableScale
              onPress={() => setEditing(NEW_SERVICE)}
              className="flex-row items-center justify-center gap-2 rounded-xl border border-dashed border-border py-2.5"
            >
              <Ionicons name="add" size={16} color="#FD5001" />
              <ThemedText className="text-sm font-semibold text-primary">
                Adicionar serviço
              </ThemedText>
            </PressableScale>
          ) : null}
        </View>

        {canEdit ? (
          <View className="gap-2">
            <ThemedText className="text-xs text-foreground/45">
              {pending.length > 0
                ? 'Em cinza está o que é comum no seu ramo. Toque para mudar.'
                : 'Confere se está tudo certo e toque em Pronto.'}
            </ThemedText>
            {onAdvance ? (
              <PressableScale
                onPress={() => void handleConfirm()}
                disabled={patch.isPending}
                className="items-center rounded-full bg-primary py-2.5"
              >
                <ThemedText className="text-sm font-semibold text-white">
                  {patch.isPending
                    ? 'Salvando...'
                    : (data.confirmLabel ?? 'Pronto')}
                </ThemedText>
              </PressableScale>
            ) : null}
          </View>
        ) : null}
      </WidgetCard>

      <ServiceEditSheet
        service={editing}
        interval={data.defaultInterval}
        onClose={() => setEditing(null)}
      />
    </>
  );
}
